/**
 * MotorBox Full-Screen Product Configurator
 * Automotive-style immersive configuration experience
 */

class ProductConfiguratorFullscreen {
  constructor(container, productData) {
    this.container = container;
    this.productData = productData;
    this.selectedOptions = [];
    this.currentVariant = null;
    this.isOpen = false;

    this.init();
  }

  init() {
    if (!this.container || !this.productData) {
      console.error('ProductConfiguratorFullscreen: Missing container or product data');
      return;
    }

    // Initialize selected options
    this.initializeSelectedOptions();

    // Bind event listeners
    this.bindEvents();

    // Find and set current variant
    this.updateVariant();
  }

  initializeSelectedOptions() {
    const product = this.productData;
    const currentVariant = product.selected_or_first_available_variant;

    if (currentVariant && currentVariant.options) {
      this.selectedOptions = [...currentVariant.options];
    } else if (product.options) {
      this.selectedOptions = product.options.map(option => {
        return option.values ? option.values[0] : '';
      });
    }
  }

  bindEvents() {
    // Open configurator
    const triggerBtn = document.querySelector('[data-configurator-trigger]');
    if (triggerBtn) {
      triggerBtn.addEventListener('click', () => this.open());
    }

    // Close configurator
    const closeBtn = this.container.querySelector('[data-configurator-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Option selection
    const optionButtons = this.container.querySelectorAll('[data-option-value]');
    optionButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const optionValue = button.dataset.optionValue;
        const optionIndex = parseInt(button.dataset.optionIndex);
        this.selectOption(optionIndex, optionValue);
      });
    });

    // Image navigation dots
    const imageDots = this.container.querySelectorAll('[data-image-index]');
    imageDots.forEach(dot => {
      dot.addEventListener('click', () => {
        const imageSrc = dot.dataset.imageSrc;
        this.changeImage(imageSrc);

        // Update active dot
        imageDots.forEach(d => d.classList.remove('active'));
        dot.classList.add('active');
      });
    });

    // Add to cart
    const addToCartBtn = this.container.querySelector('[data-configurator-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => this.addToCart());
    }
  }

  open() {
    this.container.classList.add('active');
    this.isOpen = true;
    document.body.style.overflow = 'hidden';

    // Dispatch event
    document.dispatchEvent(new CustomEvent('configurator:opened'));
  }

  close() {
    this.container.classList.remove('active');
    this.isOpen = false;
    document.body.style.overflow = '';

    // Dispatch event
    document.dispatchEvent(new CustomEvent('configurator:closed'));
  }

  selectOption(optionIndex, optionValue) {
    // Update selected options
    this.selectedOptions[optionIndex] = optionValue;

    // Update UI - remove active class from all options at this index
    const optionContainer = this.container.querySelector(`[data-option-index="${optionIndex}"]`);
    if (optionContainer) {
      const allOptions = optionContainer.querySelectorAll('[data-option-value]');
      allOptions.forEach(opt => opt.classList.remove('active'));

      // Add active class to selected option
      const selectedElement = optionContainer.querySelector(`[data-option-value="${optionValue}"]`);
      if (selectedElement) {
        selectedElement.classList.add('active');
      }
    }

    // Update selected value display
    const selectedValueDisplay = this.container.querySelector(`[data-selected-value-${optionIndex}]`);
    if (selectedValueDisplay) {
      selectedValueDisplay.textContent = optionValue;
    }

    // Update summary
    const summaryValue = this.container.querySelector(`[data-summary-value-${optionIndex}]`);
    if (summaryValue) {
      summaryValue.textContent = optionValue;
    }

    // Find and update variant
    this.updateVariant();

    // Dispatch custom event
    this.dispatchChangeEvent();
  }

  updateVariant() {
    const variant = this.findVariantByOptions(this.selectedOptions);
    this.currentVariant = variant;

    if (variant) {
      // Update price
      this.updatePrice(variant);

      // Update availability
      this.updateAvailability(variant);

      // Update add to cart button
      this.updateAddToCartButton(variant);

      // Update product image if variant has featured image
      if (variant.featured_image && variant.featured_image.src) {
        this.changeImage(variant.featured_image.src);
      }
    }
  }

  findVariantByOptions(options) {
    const variants = this.productData.variants || [];

    return variants.find(variant => {
      if (!variant.options || variant.options.length !== options.length) {
        return false;
      }

      return variant.options.every((option, index) => {
        return option === options[index];
      });
    });
  }

  updatePrice(variant) {
    const priceElement = this.container.querySelector('[data-configurator-price]');

    if (priceElement && variant.price) {
      const formattedPrice = this.formatMoney(variant.price);
      priceElement.textContent = formattedPrice;

      // Add subtle animation
      priceElement.style.animation = 'none';
      setTimeout(() => {
        priceElement.style.animation = 'pulse 0.3s ease';
      }, 10);
    }
  }

  updateAvailability(variant) {
    const availabilityContainer = this.container.querySelector('[data-configurator-availability]');

    if (!availabilityContainer) return;

    if (variant.available) {
      availabilityContainer.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="var(--color-primary)"/>
        </svg>
        <span>In Stock - Ready to Ship</span>
      `;
    } else {
      availabilityContainer.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="#ff5555"/>
        </svg>
        <span>Out of Stock - Notify When Available</span>
      `;
    }
  }

  updateAddToCartButton(variant) {
    const addToCartBtn = this.container.querySelector('[data-configurator-add-to-cart]');

    if (!addToCartBtn) return;

    if (variant.available) {
      addToCartBtn.disabled = false;
      addToCartBtn.textContent = 'Add to Cart';
    } else {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = 'Sold Out';
    }
  }

  changeImage(imageSrc) {
    const mainImage = this.container.querySelector('[data-configurator-image]');

    if (!mainImage) return;

    // Add transition class
    mainImage.classList.add('changing');

    // Wait for fade out, then change image
    setTimeout(() => {
      mainImage.src = imageSrc;

      // Wait for image to load, then fade in
      mainImage.onload = () => {
        mainImage.classList.remove('changing');
      };
    }, 250);
  }

  async addToCart() {
    if (!this.currentVariant || !this.currentVariant.available) {
      return;
    }

    const addToCartBtn = this.container.querySelector('[data-configurator-add-to-cart]');
    const originalText = addToCartBtn.textContent;

    try {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = 'Adding...';

      // Add to cart via window.cart if available
      if (window.cart && typeof window.cart.addItem === 'function') {
        await window.cart.addItem(this.currentVariant.id, 1);

        // Show success
        addToCartBtn.textContent = 'Added!';

        // Close configurator after delay
        setTimeout(() => {
          this.close();
          addToCartBtn.textContent = originalText;
          addToCartBtn.disabled = false;
        }, 1000);

      } else {
        // Fallback to direct Shopify API
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: this.currentVariant.id,
            quantity: 1
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Show success
        addToCartBtn.textContent = 'Added!';

        // Close configurator after delay
        setTimeout(() => {
          this.close();
          addToCartBtn.textContent = originalText;
          addToCartBtn.disabled = false;
        }, 1000);
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('There was an error adding the product to your cart. Please try again.');
      addToCartBtn.textContent = originalText;
      addToCartBtn.disabled = false;
    }
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents);
    }

    const dollars = cents / 100;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  }

  dispatchChangeEvent() {
    const event = new CustomEvent('configurator:change', {
      detail: {
        selectedOptions: this.selectedOptions,
        currentVariant: this.currentVariant
      }
    });

    document.dispatchEvent(event);
  }

  // Public API
  getSelectedOptions() {
    return this.selectedOptions;
  }

  getCurrentVariant() {
    return this.currentVariant;
  }

  setOption(optionIndex, optionValue) {
    this.selectOption(optionIndex, optionValue);
  }

  isFullscreenOpen() {
    return this.isOpen;
  }
}

// Add CSS animation for price pulse
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
`;
document.head.appendChild(style);

// Initialize configurator when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const configuratorContainer = document.querySelector('[data-configurator-fullscreen]');

  if (configuratorContainer && typeof window.productData !== 'undefined') {
    window.productConfiguratorFullscreen = new ProductConfiguratorFullscreen(
      configuratorContainer,
      window.productData
    );
  }
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductConfiguratorFullscreen;
}
